if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.addEventListener('pointerdown', (event) => {
          const sizeTrack = event.target?.closest?.('.vima-size-track');
          if (sizeTrack) this.variantSizeTrackScrollPosition = sizeTrack.scrollLeft;
        }, { capture: true });
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        // VIMA (T-20261020-10) : sans ?variant= dans l'URL, aucune pointure n'est
        // présélectionnée ; le parent choisit lui-même la pointure.
        // Exception : venue du calculateur (URL, fiche en panneau ou pointure confirmée) → on garde.
        this.vimaKeepSize = this.vimaShouldKeepSize();
        if (!this.vimaKeepSize) this.vimaUncheckSizes();

        // Ensure the initially selected available variant is immediately reflected
        // in the product form. Without this, the add-to-cart button can remain in
        // its temporary disabled/grey state until the first variant interaction.
        // product-form.js est chargé après product-info.js : on attend sa définition
        // (corrige « toggleSubmitButton is not a function »).
        customElements.whenDefined('product-form').then(() => {
          if (!this.isConnected) return;
          const initialVariant = this.getLocallySelectedVariant();
          if (initialVariant?.id) {
            this.updateVariantInputs(initialVariant.id);
            const variantInput = this.productForm?.variantIdInput;
            if (variantInput) variantInput.disabled = !initialVariant.available;
            this.vimaToggleSubmit(
              !initialVariant.available,
              initialVariant.available ? undefined : window.variantStrings.soldOut
            );
          }
          this.vimaApplySizeState();
        });

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      getLocallySelectedVariant() {
        const picker = this.variantSelectors;
        const data = this.parseJsonScript(picker, '[data-vima-morphology-map]');
        if (!picker || !data || !Array.isArray(data.optionNames) || !Array.isArray(data.variants)) return null;

        const selectedByName = new Map();
        picker.querySelectorAll('[data-option-name]').forEach((node) => {
          const name = node.dataset.optionName;
          if (!name) return;
          if (node.matches('input[type="radio"]') && node.checked) selectedByName.set(name, node.value);
          if (node.matches('option') && node.selected) selectedByName.set(name, node.value);
        });

        if (selectedByName.size < data.optionNames.length) return null;

        return data.variants.find((candidate) =>
          data.optionNames.every((name, index) =>
            String(candidate.options?.[index] ?? '') === String(selectedByName.get(name) ?? '')
          )
        ) || null;
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const vimaRequestValues = this.vimaPrepareSizeChoice(event.target);
        if (vimaRequestValues) selectedOptionValues = vimaRequestValues;

        this.resetProductFormState();

        const localVariant = this.getLocallySelectedVariant();
        if (localVariant?.id) {
          this.updateVariantInputs(localVariant.id);
          const variantInput = this.productForm?.variantIdInput;
          if (variantInput) variantInput.disabled = !localVariant.available;
          this.vimaToggleSubmit(
            !localVariant.available,
            localVariant.available ? undefined : window.variantStrings.soldOut
          );
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        this.vimaToggleSubmit(true);
        if (typeof productForm?.handleErrorMessage === 'function') productForm.handleErrorMessage();
      }

      // ---- VIMA (T-20261020-10) : état de la pointure --------------------------
      static get observedAttributes() {
        return ['data-vima-size-confirmed'];
      }

      // Le calculateur (panneau de résultats, fiche, ?vima_from_calculator=1) pose
      // data-vima-size-confirmed="true" puis coche la pointure recommandée sans événement
      // change : on resynchronise le formulaire pour que cette pointure reste choisie.
      attributeChangedCallback(name, oldValue, newValue) {
        if (name !== 'data-vima-size-confirmed' || newValue !== 'true') return;
        this.vimaKeepSize = true;
        window.clearTimeout(this.vimaConfirmedTimer);
        this.vimaConfirmedTimer = window.setTimeout(() => this.vimaResyncConfirmedSize(), 0);
      }

      vimaShouldKeepSize() {
        const params = new URL(window.location.href).searchParams;
        return (
          params.has('variant') ||
          params.get('vima_from_calculator') === '1' ||
          this.dataset.vimaSizeConfirmed === 'true' ||
          Boolean(this.closest('.vima-product-sheet, [data-vima-product-sheet]'))
        );
      }

      vimaResyncConfirmedSize() {
        if (!this.isConnected || !this.vimaSizeTrack?.querySelector('input[type="radio"]:checked')) return;
        const variant = this.getLocallySelectedVariant();
        if (!variant?.id) return;
        const idInput = this.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
        if (idInput && String(idInput.value) !== String(variant.id)) this.updateVariantInputs(variant.id);
        const variantInput = this.productForm?.variantIdInput;
        if (variantInput) variantInput.disabled = !variant.available;
        this.vimaToggleSubmit(!variant.available, variant.available ? undefined : window.variantStrings.soldOut);
        this.vimaApplySizeState();
      }

      vimaToggleSubmit(disable, text) {
        const productForm = this.productForm;
        if (typeof productForm?.toggleSubmitButton === 'function') productForm.toggleSubmitButton(disable, text);
      }

      get vimaSizeTrack() {
        return this.variantSelectors?.querySelector('.vima-size-track') || null;
      }

      vimaSizeInputs() {
        return Array.from(this.vimaSizeTrack?.querySelectorAll('input[type="radio"]') || []);
      }

      vimaUncheckSizes() {
        this.vimaSizeInputs().forEach((input) => {
          input.checked = false;
        });
      }

      // Variantes compatibles avec les options autres que la pointure (ex. la couleur choisie).
      vimaColourVariants(data, sizeName) {
        const picker = this.variantSelectors;
        if (!picker || !data || !Array.isArray(data.optionNames) || !Array.isArray(data.variants)) return [];
        const selected = new Map();
        picker.querySelectorAll('[data-option-name]').forEach((node) => {
          const name = node.dataset.optionName;
          if (!name || name === sizeName) return;
          if (node.matches('input[type="radio"]') && node.checked) selected.set(name, node.value);
          if (node.matches('option') && node.selected) selected.set(name, node.value);
        });
        return data.variants.filter((variant) =>
          data.optionNames.every(
            (name, index) => !selected.has(name) || String(variant.options?.[index] ?? '') === selected.get(name)
          )
        );
      }

      // Appelé avant la requête Dawn. Changement de couleur : on garde la pointure si elle
      // est disponible dans la nouvelle couleur, sinon on la désélectionne. Pour éviter un
      // rendu serveur « Épuisé », la requête demande la première pointure disponible de la
      // couleur ; la pointure est ensuite décochée par vimaApplySizeState().
      vimaPrepareSizeChoice(target) {
        const track = this.vimaSizeTrack;
        if (!track) return null;
        if (track.contains(target)) {
          this.vimaKeepSize = true;
          return null;
        }

        const sizeInputs = this.vimaSizeInputs();
        const checkedSize = sizeInputs.find((input) => input.checked);
        const data = this.parseJsonScript(this.variantSelectors, '[data-vima-morphology-map]');
        const sizeName = sizeInputs[0]?.dataset.optionName;
        const sizeIndex = data?.optionNames?.indexOf(sizeName) ?? -1;
        if (!data || sizeIndex < 0) {
          this.vimaKeepSize = Boolean(checkedSize);
          return null;
        }

        const candidates = this.vimaColourVariants(data, sizeName);
        if (
          checkedSize &&
          candidates.some((variant) => variant.available && String(variant.options?.[sizeIndex]) === checkedSize.value)
        ) {
          this.vimaKeepSize = true;
          return null;
        }

        this.vimaKeepSize = false;
        this.vimaUncheckSizes();
        const firstAvailable = candidates.find((variant) => variant.available);
        const requestSize =
          firstAvailable && sizeInputs.find((input) => input.value === String(firstAvailable.options?.[sizeIndex]));
        if (!requestSize) return null;

        requestSize.checked = true;
        const values = this.variantSelectors.selectedOptionValues;
        requestSize.checked = false;
        return values;
      }

      // Appelé après chaque rendu (et au chargement) : applique l'état « pointure à choisir »
      // ou corrige le libellé d'une pointure épuisée choisie explicitement.
      vimaApplySizeState() {
        const track = this.vimaSizeTrack;
        if (!track) return;
        if (!this.vimaKeepSize) this.vimaUncheckSizes();

        const data = this.parseJsonScript(this.variantSelectors, '[data-vima-morphology-map]');
        const sizeName = this.vimaSizeInputs()[0]?.dataset.optionName;
        const colourHasStock = this.vimaColourVariants(data, sizeName).some((variant) => variant.available);
        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        const checkedSize = track.querySelector('input[type="radio"]:checked');

        if (!checkedSize) {
          this.setAttribute('data-vima-size-missing', '');
          const idInput = this.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
          if (idInput && idInput.value !== '') {
            idInput.value = '';
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
          if (colourHasStock) {
            this.vimaToggleSubmit(true, 'Choisir une pointure');
            priceRoot?.querySelector('.price--sold-out')?.classList.remove('price--sold-out');
          } else {
            this.vimaToggleSubmit(true, window.variantStrings.soldOut);
          }

          if (this.dataset.updateUrl !== 'false') {
            const url = new URL(window.location.href);
            if (url.searchParams.has('variant')) {
              url.searchParams.delete('variant');
              window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
            }
          }
          return;
        }

        this.removeAttribute('data-vima-size-missing');
        const variant = this.getLocallySelectedVariant();
        if (variant && !variant.available && colourHasStock) {
          this.vimaToggleSubmit(true, 'Pointure épuisée');
          priceRoot?.querySelector('.price--sold-out')?.classList.remove('price--sold-out');
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        const sizeTrackScrollPosition = this.variantSizeTrackScrollPosition;
        this.variantSizeTrackScrollPosition = undefined;
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);

            if (Number.isFinite(sizeTrackScrollPosition)) {
              window.requestAnimationFrame(() => {
                const sizeTracks = Array.from(this.querySelectorAll('.vima-size-track'));
                const sizeTrack = sizeTracks.find((track) => track.offsetParent !== null) || sizeTracks[0];
                if (sizeTrack) sizeTrack.scrollLeft = sizeTrackScrollPosition;
              });
            }

            this.querySelector(`#${CSS.escape(targetId)}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.vimaToggleSubmit(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          this.vimaApplySizeState();
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.vimaToggleSubmit(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');

        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => spinner?.classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;

        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
